import { z } from "zod";
import { elementComponentrySchema, environmentReadingSchema } from "./environment";
import { EXTENDED_CAPABILITIES } from "./extended-registry";
import {
  planDesignReviewInputSchema, taskPlanAmendmentInputSchema, taskPlanProgressInputSchema, taskPlanProposalSchema } from "./orchestration";
import { skillLoadInputSchema, skillLoadOutputSchema } from "./skills";

export type CapabilityPlane = "client" | "server";
export type CapabilityEffect = "read" | "mutate" | "destructive";

export interface RestCapabilityHandler {
  kind: "rest";
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  /** GET fields that belong in the query string instead of the route path. */
  query?: readonly string[];
  /** The endpoint returns a job that can subsequently be followed with job.wait. */
  startsJob?: boolean;
}

export interface ClientCapabilityHandler {
  kind: "client";
  action: string;
}

export type CapabilityHandler = RestCapabilityHandler | ClientCapabilityHandler;

export interface CapabilityDefinition {
  name: string;
  title: string;
  group: string;
  plane: CapabilityPlane;
  summary: string;
  progressLabel: string;
  input: z.ZodType;
  output: z.ZodType;
  effect: CapabilityEffect;
  confirm: boolean;
  /** Extra entries marked core remain visible without a capability search. */
  core?: boolean;
  handler?: CapabilityHandler;
  examples?: Array<{ request: string; input: unknown }>;
  guide?: CapabilityOperationalGuide;
}

export interface CapabilityOperationalGuide {
  useWhen: string[];
  avoidWhen?: string[];
  prerequisites?: string[];
  procedure?: string[];
  success?: string[];
  recovery?: string[];
}

export interface AgentToolDefinition {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
}

export interface CapabilityInputIssue {
  path: string;
  code: string;
  message: string;
}

export interface CapabilityNameResolution {
  name: string;
  strategy: "exact" | "provider-encoding" | "semantic-read-alias";
}

export type CapabilityInputValidation =
  | { valid: true; data: unknown }
  | {
      valid: false;
      error: {
        code: "UNKNOWN_CAPABILITY" | "INVALID_CAPABILITY_INPUT";
        capability: string;
        message: string;
        issues: CapabilityInputIssue[];
        retryable: boolean;
      };
    };

const vec3Mm = z.tuple([
  z.number().finite().min(-1_000_000).max(1_000_000),
  z.number().finite().min(-1_000_000).max(1_000_000),
  z.number().finite().min(-1_000_000).max(1_000_000),
]).describe("Coordenadas [X, Y, Z] em milímetros na origem do documento.");

const elementId = z.string().trim().min(1).max(160)
  .describe("Identificador do elemento, obtido por scene.read, scene.element.read ou por uma ação anterior.");

const dimensionsMm = z.object({
  width: z.number().finite().min(1).max(100_000).optional().describe("Largura no eixo local X, em milímetros."),
  height: z.number().finite().min(1).max(100_000).optional().describe("Altura no eixo local Y, em milímetros."),
  depth: z.number().finite().min(1).max(100_000).optional().describe("Profundidade no eixo local Z, em milímetros."),
}).refine((value) => Object.values(value).some((item) => item !== undefined), {
  message: "Informe ao menos uma dimensão.",
});

const renderedGeometryStatus = z.object({
  status: z.enum(["ready", "pending"]),
  source: z.enum(["measured", "parametric_exact", "parametric_fallback"]),
  pose: z.literal("closed"),
  elementRevision: z.number().int().nonnegative(),
  measuredRevision: z.number().int().nonnegative().nullable(),
});

const elementSnapshot = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.string(),
  translationMm: vec3Mm,
  rootTranslationMm: vec3Mm.optional(),
  physicalCenterMm: vec3Mm.optional(),
  rotationRad: z.tuple([z.number(), z.number(), z.number()]),
  dimensionsMm: z.object({ width: z.number(), height: z.number(), depth: z.number() }),
  renderedDimensionsMm: z.object({ width: z.number(), height: z.number(), depth: z.number() }).optional()
    .describe("Dimensões físicas fechadas nos eixos locais do elemento."),
  renderedBoundsMm: z.object({ minimum: vec3Mm, maximum: vec3Mm }).optional()
    .describe("Alias legado dos limites físicos locais; não são coordenadas mundiais."),
  renderedLocalBoundsMm: z.object({ minimum: vec3Mm, maximum: vec3Mm }).optional()
    .describe("Limites físicos fechados relativos à origem paramétrica local do elemento."),
  renderedGeometry: renderedGeometryStatus.optional(),
  materialId: z.string(),
  materialAssignments: z.record(z.string(), z.object({ materialId: z.string(), revision: z.string() })),
  catalogRef: z.object({ itemId: z.string(), revision: z.string() }).optional(),
  componentry: elementComponentrySchema.optional(),
  locked: z.boolean(),
  visible: z.boolean(),
});

const dynamicElementInfo = z.object({
  interactions: z.array(z.object({
    path: z.string(),
    name: z.string(),
    state: z.enum(["open", "closed", "unknown"]).optional(),
  })),
});

const semanticPlacementRelation = z.enum(["beside", "above", "below", "in_front_of", "behind", "stacked", "against"]);
const semanticPlacementSide = z.enum(["auto", "left", "right", "front", "back"]);
const semanticPlacementAlignment = z.enum(["auto", "front", "back", "center"]);
const semanticPlacementSupport = z.enum(["auto", "floor", "wall", "element"]);
const semanticPlacementClearance = z.object({
  minimum: z.number().finite().min(0).max(100_000),
  target: z.number().finite().min(0).max(100_000),
  maximum: z.number().finite().min(0).max(100_000),
}).superRefine((value, context) => {
  if (value.minimum > value.target || value.target > value.maximum) {
    context.addIssue({ code: "custom", message: "A folga-alvo precisa ficar entre a mínima e a máxima." });
  }
});
const semanticPlacementBase = {
  referenceElementId: elementId.describe("Elemento usado como referência para calcular a implantação. Em scene.module.install com apoio no piso, também pode ser uma parede arquitetônica real lida da cena."),
  relation: semanticPlacementRelation.describe("Relação espacial desejada entre o alvo e a referência."),
  preferredSide: semanticPlacementSide.default("auto").describe("Preferência explícita; auto compara os lados geometricamente válidos. left/right são os lados de quem olha a frente da referência a partir do ambiente. Quando o lado é ambíguo, prefira towardsWallId, towardsElementId ou awayFromElementId."),
  towardsWallId: z.string().trim().min(1).max(160).optional()
    .describe("Forma inequívoca de escolher o lado: o lado da referência mais próximo desta parede. Substitui preferredSide."),
  towardsElementId: z.string().trim().min(1).max(160).optional()
    .describe("O lado da referência mais próximo deste elemento. Substitui preferredSide."),
  awayFromElementId: z.string().trim().min(1).max(160).optional()
    .describe("O lado da referência mais distante deste elemento (por exemplo, o lado livre, longe de um conjunto já ocupado). Substitui preferredSide."),
  alignment: semanticPlacementAlignment.default("auto").describe("auto herda parede/fundo quando houver apoio comum; caso contrário compara alinhamentos profissionais."),
  support: semanticPlacementSupport.default("auto").describe("Apoio físico esperado para o alvo."),
  clearanceMm: semanticPlacementClearance.describe("Folga profissional já decidida para esta relação, em milímetros. A ferramenta não inventa uma distância quando o briefing a deixou livre; para elementos suspensos sobre outro móvel, use a orientação de tipologia devolvida (vão livre mínimo e típico) — folga zero é empilhamento, não instalação."),
  bottomHeightMm: z.number().finite().min(0).max(100_000).optional()
    .describe("Altura da face inferior do alvo em relação ao piso, em milímetros, para relações above/below/stacked. É a forma como um instalador cota qualquer elemento suspenso — módulo superior, espelho, coifa, pendente (por exemplo 1.450 mm). Quando informada, prevalece sobre clearanceMm.target: a ferramenta converte em folga vertical em relação à referência e devolve o valor efetivo."),
  wallId: z.string().trim().min(1).max(160).optional().describe("Parede obrigatória quando já for conhecida; auto infere a parede da referência."),
  roomId: z.string().trim().min(1).max(160).optional(),
};
const heightCandidateOutput = z.object({
  label: z.string(),
  datumKind: z.enum(["use-band", "existing-line", "ceiling", "opening-head"]),
  datumReference: z.string().nullable(),
  bottomHeightMm: z.number(),
  topHeightMm: z.number().nullable(),
  clearanceMm: z.number().nullable(),
  reach: z.string().nullable(),
  notes: z.array(z.string()),
});
const typologyGuidanceOutput = z.object({
  targetTypology: z.record(z.string(), z.unknown()),
  referenceTypology: z.record(z.string(), z.unknown()).nullable(),
  rule: z.record(z.string(), z.unknown()).nullable(),
  guidance: z.string(),
  effectiveClearanceMm: z.record(z.string(), z.number()).nullable(),
  bottomHeightMm: z.number().nullable(),
  warnings: z.array(z.string()),
  activity: z.object({
    activity: z.string(),
    label: z.string(),
    confidence: z.string(),
    basis: z.string(),
  }).nullable().optional()
    .describe("Atividade provável na superfície da referência (trabalho em pé, apoio, assento…) e sua evidência."),
  band: z.object({
    bottomHeightMm: z.tuple([z.number(), z.number()]).nullable(),
    clearanceMm: z.tuple([z.number(), z.number()]).nullable(),
    lineMayOverride: z.boolean(),
    basis: z.array(z.string()),
  }).nullable().optional()
    .describe("Faixa de face inferior e de vão para esta atividade; lineMayOverride diz se uma linha existente da parede pode substituir a faixa."),
  wallLines: z.array(z.object({
    heightMm: z.number(),
    kind: z.string(),
    family: z.string(),
    sourceName: z.string().optional(),
  })).optional()
    .describe("Linhas que a parede da referência já tem (topos e bases de outros elementos, teto, vergas)."),
  heightCandidates: z.array(heightCandidateOutput).optional()
    .describe("Cotas candidatas para a face inferior do alvo: faixa de uso e cada linha existente da parede, com alcance. Compare-as no plano e registre a escolhida com datum."),
  reach: z.string().nullable().optional()
    .describe("Leitura de alcance do conteúdo na cota pedida."),
  operation: z.string().nullable().optional()
    .describe("Objeção de envelope de operação (folha aberta na zona de cabeça) na cota pedida, quando houver."),
}).describe("Orientação de instalação do CalcPlan para esta relação — régua do par, da pessoa e da parede: dado do produto, não instrução externa. warnings lista o que a implantação escolhida contraria; a leitura, a conferência de instalação e o laudo aplicam a mesma régua.");
const semanticPlacementCandidate = z.object({
  candidateId: z.string(),
  label: z.string(),
  positionMm: vec3Mm,
  rotationDeg: vec3Mm,
  side: z.enum(["left", "right", "front", "back", "center"]),
  alignment: z.enum(["front", "back", "center"]),
  wallId: z.string().nullable(),
  geometryScore: z.number(),
  designScore: z.number(),
  totalScore: z.number(),
  valid: z.boolean(),
  reasons: z.array(z.string()),
  clearance: z.record(z.string(), z.unknown()),
  grounded: z.record(z.string(), z.unknown()),
  installation: z.record(z.string(), z.unknown()),
});

function defineCapability(definition: CapabilityDefinition): CapabilityDefinition {
  if (definition.effect === "destructive" && !definition.confirm) {
    throw new Error(`A capacidade destrutiva ${definition.name} precisa de confirmação.`);
  }
  return definition;
}

export const CORE_CAPABILITIES: readonly CapabilityDefinition[] = [
  defineCapability({
    name: "skill.load",
    title: "Carregar método profissional",
    group: "skills",
    plane: "server",
    summary: "Ativa para a tarefa atual uma skill profissional publicada pelo CalcPlan. Use o slug exato informado no catálogo do sistema quando o método especializado melhorar uma tarefa criativa, espacial ou técnica. O conteúdo é carregado pelo servidor na próxima etapa; esta ação não altera o projeto e não concede novas permissões.",
    progressLabel: "Preparando o método de projeto…",
    input: skillLoadInputSchema,
    output: skillLoadOutputSchema,
    effect: "read",
    confirm: false,
    core: true,
    handler: { kind: "rest", method: "POST", path: "/ai/skills/:slug/activate" },
  }),
  defineCapability({
    name: "scene.read",
    title: "Ler a cena",
    group: "cena",
    plane: "client",
    summary: "Lê um resumo estruturado da cena aberta, sempre em milímetros: paredes, vãos, ambientes, módulos, materiais e seleção. Use antes de supor medidas, posição ou identificadores. Não altera o projeto.",
    progressLabel: "Conferindo o ambiente…",
    input: z.object({
      detail: z.enum(["summary", "architecture", "elements", "all"]).default("summary")
        .describe("Parte da cena a devolver. Use all somente quando a tarefa realmente precisar."),
    }),
    output: z.record(z.string(), z.unknown()),
    effect: "read",
    confirm: false,
  }),
  defineCapability({
    name: "scene.environment.read",
    title: "Ler o ambiente (linhas, função, superfícies)",
    group: "cena",
    plane: "client",
    core: true,
    summary: "Leitura estruturada do ambiente que um projetista faz antes de cotar: função do cômodo (declarada, inferida ou indefinida, com evidência), perfil de alcance dos usuários, pé-direito, e por parede as linhas existentes (topos e bases dos elementos fixados ou apoiados, teto, vergas), os elementos com faixa vertical e tipologia observada, e as superfícies de uso com a atividade provável. Obrigatória antes de planejar qualquer relação vertical ou implantação livre. Não altera o projeto.",
    progressLabel: "Lendo as linhas do ambiente…",
    input: z.object({
      roomId: z.string().trim().min(1).max(160).optional()
        .describe("Ambiente a ler; omita para o ambiente dos alvos ou o único da cena."),
      wallIds: z.array(z.string().trim().min(1).max(160)).max(12).optional()
        .describe("Paredes de interesse; omita para todas."),
      focusElementIds: z.array(elementId).max(20).optional()
        .describe("Elementos de referência da tarefa; a leitura destaca as paredes e superfícies deles."),
    }),
    output: environmentReadingSchema,
    effect: "read",
    confirm: false,
    guide: {
      useWhen: ["antes de decidir a cota de um elemento suspenso ou a implantação livre de qualquer elemento", "para saber quais linhas a parede já tem e para que serve a superfície de baixo"],
      avoidWhen: ["a cena não mudou desde a última leitura"],
      prerequisites: ["scene.read ou leitura dos alvos"],
      procedure: ["ler uma vez", "registrar no plano a função/atividade adotada (assumptions) e a linha escolhida (datum)"],
      success: ["linhas por parede e atividade das superfícies conhecidas antes do plano"],
      recovery: ["se a função ficar indefinida, declare a premissa no plano; pergunte só se duas funções plausíveis levarem a cotas incompatíveis"],
    },
  }),
  defineCapability({
    name: "scene.selection.set",
    title: "Selecionar elementos",
    group: "cena",
    plane: "client",
    summary: "Troca a seleção do editor pelos identificadores informados. Use para alinhar a interface ao foco do trabalho; não move nem modifica geometria.",
    progressLabel: "Selecionando no projeto…",
    input: z.object({ elementIds: z.array(elementId).max(100) }),
    output: z.object({ selectedIds: z.array(z.string()) }),
    effect: "mutate",
    confirm: false,
  }),
  defineCapability({
    name: "scene.module.add",
    title: "Inserir módulo na cena",
    group: "cena",
    plane: "client",
    summary: "Insere uma instância pretendida de um módulo do catálogo na posição informada em milímetros. A posição é o centro geométrico do módulo no documento; para apoiar no piso, Y costuma ser metade da altura. O item precisa ter sido localizado com catalog.item.search. Repetir catalogItemId + instanceKey na mesma entrega reutiliza o elemento já criado, em vez de duplicá-lo. Use chaves diferentes somente quando a entrega pedir unidades realmente distintas. Devolve o elementId estável para as próximas ações.",
    progressLabel: "Inserindo o módulo…",
    input: z.object({
      catalogItemId: z.string().trim().min(1).max(160).describe("ID do item retornado por catalog.item.search."),
      positionMm: vec3Mm.describe("Centro do módulo [X, Y, Z], em milímetros. Y normalmente é metade da altura quando apoiado no piso."),
      instanceKey: z.string().trim().min(1).max(120).regex(/^[a-z0-9][a-z0-9_-]*$/i).optional()
        .describe("Chave estável da unidade pretendida dentro da entrega, como modulo-principal ou cadeira-2. Omita para a unidade principal. Em uma tentativa de correção, preserve a mesma chave; não crie uma chave nova."),
    }),
    output: z.object({
      element: elementSnapshot,
      dynamic: dynamicElementInfo.optional(),
      stepId: z.string(),
      instanceKey: z.string(),
      reused: z.boolean(),
      message: z.string().optional(),
    }),
    effect: "mutate",
    confirm: false,
    examples: [{ request: "Coloque este balcão no ponto 2 m por 3 m", input: { catalogItemId: "catalog-123", positionMm: [2_000, 350, 3_000] } }],
    guide: {
      useWhen: ["o produto já foi localizado no catálogo e precisa apenas ser instanciado"],
      avoidWhen: ["a entrega pede implantação relativa completa; prefira scene.module.install", "uma instância com a mesma intenção já existe"],
      prerequisites: ["usar exatamente um catalogItemId retornado pelo catálogo", "definir uma instanceKey estável"],
      procedure: ["inserir uma vez", "continuar no elementId devolvido", "implantar ou verificar sem criar nova cópia"],
      success: ["reused=false na primeira criação ou reused=true na repetição segura"],
      recovery: ["se a posição estiver ruim, corrija o elementId existente; nunca troque instanceKey para tentar novamente"],
    },
  }),
  defineCapability({
    name: "scene.element.move",
    title: "Mover elemento",
    group: "cena",
    plane: "client",
    summary: "Move um elemento existente para uma posição absoluta, preservando rotação, tamanho e apoio registrado. Prefira physicalCenterMm para posicionar o volume visível fechado. translationMm é a origem paramétrica legada e pode diferir do centro físico em componentes assimétricos ou alinhados a uma superfície. Informe exatamente um dos dois campos. Para relações como ao lado/acima, use scene.element.place.",
    progressLabel: "Reposicionando o módulo…",
    input: z.object({
      elementId,
      physicalCenterMm: vec3Mm.optional().describe("Centro físico desejado do volume fechado [X,Y,Z], opção recomendada."),
      translationMm: vec3Mm.optional().describe("Origem paramétrica absoluta legada [X,Y,Z]; use somente quando essa origem for explicitamente conhecida."),
    }).superRefine((input, context) => {
      if (Boolean(input.physicalCenterMm) === Boolean(input.translationMm)) {
        context.addIssue({ code: "custom", message: "Informe physicalCenterMm ou translationMm, mas não ambos." });
      }
    }),
    output: z.object({ element: elementSnapshot, previousTranslationMm: vec3Mm.optional(), unchanged: z.boolean().optional() }),
    effect: "mutate",
    confirm: false,
    guide: {
      useWhen: ["há uma coordenada absoluta comprovada para o centro físico ou para a origem paramétrica"],
      avoidWhen: ["o pedido é relacional, como ao lado ou acima", "o valor é apenas um deslocamento"],
      prerequisites: ["reler o elemento e escolher physicalCenterMm por padrão"],
      procedure: ["mover uma vez", "reler", "medir apoio ou folga"],
      success: ["renderedGeometry.status=ready e centro físico no destino"],
      recovery: ["se houver relação espacial, abandone coordenadas manuais e use scene.element.place"],
    },
  }),
  defineCapability({
    name: "scene.module.install",
    title: "Instalar módulo por relação",
    group: "cena",
    plane: "client",
    core: true,
    summary: "Executa como uma única intenção idempotente a sequência profissional de inserir, dimensionar opcionalmente, implantar por relação e copiar opcionalmente o acabamento de uma referência. Use para módulos novos pedidos ao lado, acima, abaixo, à frente, atrás, empilhados ou contra outro elemento. Para um módulo de piso em ambiente vazio, referenceElementId pode ser a parede principal; towardsWallId pode indicar a segunda parede do canto e roomId o ambiente. A mesma instanceKey continua no mesmo elemento em qualquer correção.",
    progressLabel: "Instalando o módulo no conjunto…",
    input: z.object({
      catalogItemId: z.string().trim().min(1).max(160),
      instanceKey: z.string().trim().min(1).max(120).regex(/^[a-z0-9][a-z0-9_-]*$/i),
      dimensionsMm: dimensionsMm.optional().describe("Dimensão nominal já decidida; omita para preservar a dimensão do catálogo."),
      appearanceReferenceElementId: elementId.optional().describe("Elemento cuja aparência completa deve ser copiada, quando o briefing pedir a mesma cor/acabamento."),
      ...semanticPlacementBase,
    }),
    output: z.object({
      element: elementSnapshot,
      placement: semanticPlacementCandidate,
      stepId: z.string(),
      instanceKey: z.string(),
      reused: z.boolean(),
      resized: z.boolean(),
      appearanceMatched: z.boolean(),
      changed: z.boolean(),
      closedInteractionPaths: z.array(z.string()).optional()
        .describe("Folhas, portas ou gavetas que nasceram abertas e foram fechadas na instalação; o módulo é entregue fechado."),
      typologyGuidance: typologyGuidanceOutput.optional(),
    }),
    effect: "mutate",
    confirm: false,
    guide: {
      useWhen: ["um módulo novo deve ser integrado ao ambiente por relação espacial"],
      avoidWhen: ["o módulo já existe", "o usuário forneceu coordenadas absolutas que precisam ser reproduzidas literalmente"],
      prerequisites: ["produto real do catálogo", "instanceKey estável", "referência existente — elemento ou parede arquitetônica para apoio no piso", "decisões livres resolvidas no plano"],
      procedure: ["instalar uma vez", "usar o elementId devolvido", "conferir instalação e revisão visual"],
      success: ["um único elemento vivo, placement válido e renderedGeometry.status=ready"],
      recovery: ["em ambiente vazio use a parede real como referenceElementId, a parede lateral do canto em towardsWallId e support=floor", "corrigir a mesma instância; nunca criar outra unidade para resolver posição, tamanho ou acabamento"],
    },
  }),
  defineCapability({
    name: "scene.element.nudge",
    title: "Ajustar posição por deslocamento",
    group: "cena",
    plane: "client",
    summary: "Desloca um elemento a partir da posição atual pelo vetor [ΔX, ΔY, ΔZ] em milímetros. Use para corrigir uma folga medida ou fazer um ajuste relativo; não converta o deslocamento em posição absoluta.",
    progressLabel: "Ajustando a posição…",
    input: z.object({
      elementId,
      deltaMm: vec3Mm.describe("Deslocamento relativo [ΔX, ΔY, ΔZ] em milímetros, somado à posição atual."),
    }),
    output: z.object({ element: elementSnapshot, previousTranslationMm: vec3Mm, appliedDeltaMm: vec3Mm, unchanged: z.boolean().optional() }),
    effect: "mutate",
    confirm: false,
    guide: {
      useWhen: ["há uma diferença medida e um único deslocamento corretivo objetivo"],
      avoidWhen: ["a posição precisa ser descoberta", "não há um delta comprovado", "o delta é zero"],
      prerequisites: ["medir a folga e definir o eixo correto"],
      procedure: ["aplicar uma correção", "reler ou medir", "parar se o objetivo foi atingido"],
      success: ["a métrica-alvo melhora sem criar nova colisão"],
      recovery: ["não experimente deltas opostos; use scene.element.place quando a implantação for relacional"],
    },
  }),
  defineCapability({
    name: "scene.placement.resolve",
    title: "Resolver implantação por relação",
    group: "conferência",
    plane: "client",
    core: true,
    summary: "Calcula e classifica posições geometricamente viáveis sem alterar a cena. Use para pedidos relacionais como ao lado, acima, atrás, à frente, empilhado ou contra outro móvel. O CalcPlan deriva coordenadas, rotação, apoio, parede, folgas e colisões; a recomendação é técnica e não substitui a escolha de layout feita com as skills e as imagens.",
    progressLabel: "Resolvendo a melhor implantação…",
    input: z.object({
      elementId: elementId.optional(),
      catalogItemId: z.string().trim().min(1).max(160).optional(),
      ...semanticPlacementBase,
      maxCandidates: z.number().int().min(1).max(8).default(4),
    }).superRefine((input, context) => {
      if (Boolean(input.elementId) === Boolean(input.catalogItemId)) {
        context.addIssue({ code: "custom", path: ["elementId"], message: "Informe elementId ou catalogItemId, mas não ambos." });
      }
      if (input.elementId && input.elementId === input.referenceElementId) {
        context.addIssue({ code: "custom", path: ["referenceElementId"], message: "O alvo e a referência precisam ser diferentes." });
      }
    }),
    output: z.object({
      recommended: semanticPlacementCandidate.nullable(),
      candidates: z.array(semanticPlacementCandidate),
      blockedReasons: z.array(z.string()),
      typologyGuidance: typologyGuidanceOutput.optional(),
    }),
    effect: "read",
    confirm: false,
    guide: {
      useWhen: ["é necessário comparar alternativas relacionais sem alterar a cena"],
      avoidWhen: ["o elemento já pode ser implantado diretamente em modo auto"],
      prerequisites: ["informar exatamente elementId ou catalogItemId e uma referência real"],
      procedure: ["resolver uma vez", "escolher recommended se válido", "aplicar com scene.element.place ou scene.module.install"],
      success: ["recommended.valid=true com apoio e folgas comprovados"],
      recovery: ["se não houver candidato, altere uma restrição material; não repita a mesma chamada"],
    },
  }),
  defineCapability({
    name: "scene.element.place",
    title: "Implantar elemento por relação",
    group: "cena",
    plane: "client",
    core: true,
    summary: "Resolve e aplica atomicamente uma posição geometricamente válida de um elemento em relação a outro. Use depois de decidir profissionalmente lado, alinhamento, suporte e folga. Em modo auto, o desempate é apenas técnico e não constitui aprovação de design; só falha quando nenhuma implantação respeita geometria e apoio.",
    progressLabel: "Implantando o módulo no melhor local…",
    input: z.object({
      elementId,
      ...semanticPlacementBase,
    }).superRefine((input, context) => {
      if (input.elementId === input.referenceElementId) {
        context.addIssue({ code: "custom", path: ["referenceElementId"], message: "O alvo e a referência precisam ser diferentes." });
      }
    }),
    output: z.object({
      element: elementSnapshot,
      placement: semanticPlacementCandidate,
      alternativesEvaluated: z.number().int().min(1),
      previousTransform: z.object({ translationMm: vec3Mm, rotationRad: vec3Mm }).optional(),
      unchanged: z.boolean().optional(),
      typologyGuidance: typologyGuidanceOutput.optional(),
    }),
    effect: "mutate",
    confirm: false,
    examples: [{
      request: "Coloque o móvel ao lado do roupeiro, preservando os 40 mm que defini",
      input: {
        elementId: "element-movel",
        referenceElementId: "element-roupeiro",
        relation: "beside",
        preferredSide: "auto",
        alignment: "auto",
        support: "floor",
        clearanceMm: { minimum: 40, target: 40, maximum: 40 },
      },
    }],
    guide: {
      useWhen: ["um elemento existente deve ser implantado por relação espacial"],
      avoidWhen: ["a instância ainda não existe; prefira scene.module.install", "há coordenadas explicitamente fixadas pelo usuário"],
      prerequisites: ["geometria atual pronta e referência existente"],
      procedure: ["aplicar em modo auto", "usar o placement devolvido", "conferir instalação e imagem"],
      success: ["placement.valid=true, elemento pronto e sem repetição da mesma estratégia"],
      recovery: ["se bloqueado, preserve o mesmo elemento e revise somente a restrição indicada"],
    },
  }),
  defineCapability({
    name: "scene.placement.evaluate",
    title: "Comparar posições sem alterar",
    group: "conferência",
    plane: "client",
    summary: "Compara de duas a quatro posições candidatas sem alterar a cena. Para um elemento já existente, informe somente elementId. Para um módulo ainda não inserido, informe somente catalogItemId. Nunca envie os dois identificadores. Mede colisões, apoio, paredes e relações com módulos vizinhos antes de uma posição livre ser executada.",
    progressLabel: "Comparando alternativas de posição…",
    input: z.object({
      elementId: elementId.optional(),
      catalogItemId: z.string().trim().min(1).max(160).optional(),
      candidates: z.array(z.object({
        label: z.string().trim().min(1).max(80),
        positionMm: vec3Mm,
        rotationDeg: vec3Mm.default([0, 0, 0]),
        support: z.enum(["floor", "wall"]).default("floor"),
        wallId: z.string().trim().min(1).max(160).optional(),
        roomId: z.string().trim().min(1).max(160).optional(),
      })).min(2).max(4),
    }).superRefine((input, context) => {
      if (Boolean(input.elementId) === Boolean(input.catalogItemId)) {
        context.addIssue({ code: "custom", path: ["elementId"], message: "Informe elementId ou catalogItemId, mas não ambos." });
      }
      input.candidates.forEach((candidate, index) => {
        if (candidate.support === "wall" && !candidate.wallId) {
          context.addIssue({ code: "custom", path: ["candidates", index, "wallId"], message: "Uma alternativa de parede precisa de wallId." });
        }
      });
    }),
    output: z.object({
      candidates: z.array(z.object({
        label: z.string(),
        positionMm: vec3Mm,
        rotationDeg: vec3Mm,
        geometryScore: z.number(),
        valid: z.boolean(),
        clearance: z.record(z.string(), z.unknown()),
        grounded: z.record(z.string(), z.unknown()),
        installation: z.record(z.string(), z.unknown()),
      })),
    }),
    effect: "read",
    confirm: false,
  }),
  defineCapability({
    name: "scene.element.mount",
    title: "Fixar elemento na parede",
    group: "cena",
    plane: "client",
    summary: "Fixa um módulo aéreo ou suspenso na face de uma parede, alinha sua rotação à parede e grava esse vínculo para os próximos movimentos. Por padrão escolhe geometricamente a face interna do ambiente, mesmo que o centro aproximado esteja do lado externo. Informe roomId em paredes entre dois ambientes; use face exterior somente quando o pedido disser isso explicitamente. Para módulos apoiados no piso, use scene.element.move.",
    progressLabel: "Fixando o módulo na parede…",
    input: z.object({
      elementId,
      wallId: z.string().trim().min(1).max(160).describe("Identificador da parede obtido por scene.read com detail architecture ou all."),
      centerMm: vec3Mm.describe("Centro aproximado desejado [X, Y, Z], em milímetros; será projetado na face da parede."),
      roomId: z.string().trim().min(1).max(160).optional().describe("Ambiente em cuja face o módulo deve ficar. Informe quando a parede separar dois ambientes."),
      face: z.enum(["interior", "exterior"]).default("interior").describe("Face da parede. Use exterior somente quando solicitado explicitamente."),
    }),
    output: z.object({
      element: elementSnapshot,
      wallId: z.string(),
      face: z.enum(["interior", "exterior"]),
      roomId: z.string().nullable(),
      adjacentRoomIds: z.array(z.string()),
      previousTransform: z.object({ translationMm: vec3Mm, rotationRad: vec3Mm }).optional(),
      unchanged: z.boolean().optional(),
    }),
    effect: "mutate",
    confirm: false,
    examples: [{ request: "Fixe o aéreo na parede com o centro a 2,35 m", input: { elementId: "element-1", wallId: "wall-4", centerMm: [250, 2_350, 5_000], face: "interior" } }],
    guide: {
      useWhen: ["um elemento suspenso precisa de vínculo real com uma parede conhecida"],
      avoidWhen: ["o elemento é apoiado no piso", "a relação completa pode ser resolvida atomicamente por scene.module.install"],
      prerequisites: ["parede obtida por scene.read", "geometria renderizada atual"],
      procedure: ["fixar na face correta", "conferir instalação", "capturar vista frontal ou oblíqua"],
      success: ["support.expected=wall, support.status=supported e renderedGeometry.status=ready"],
      recovery: ["não compense uma falha de apoio com nudges aleatórios; revise parede, face e centro físico"],
    },
  }),
  defineCapability({
    name: "scene.element.rotate",
    title: "Girar elemento",
    group: "cena",
    plane: "client",
    summary: "Define a rotação absoluta do elemento nos eixos locais X, Y e Z. Os argumentos entram em graus para facilitar pedidos de projeto; a cena grava radianos. Preserva posição e dimensões.",
    progressLabel: "Girando o módulo…",
    input: z.object({
      elementId,
      rotationDeg: z.tuple([
        z.number().finite().min(-3600).max(3600),
        z.number().finite().min(-3600).max(3600),
        z.number().finite().min(-3600).max(3600),
      ]).describe("Rotação absoluta [X, Y, Z] em graus."),
    }),
    output: z.object({ element: elementSnapshot, previousRotationRad: vec3Mm.optional(), unchanged: z.boolean().optional() }),
    effect: "mutate",
    confirm: false,
  }),
  defineCapability({
    name: "scene.element.resize",
    title: "Redimensionar elemento",
    group: "cena",
    plane: "client",
    summary: "Pede novas dimensões de um elemento em milímetros. Em módulo dinâmico a medida é uma intenção: as fórmulas podem limitar o resultado. O retorno contém a medida realmente atingida e deve ser conferido e relatado.",
    progressLabel: "Ajustando as medidas…",
    input: z.object({ elementId, dimensionsMm }),
    output: z.object({ element: elementSnapshot, requestedDimensionsMm: dimensionsMm, unchanged: z.boolean().optional() }),
    effect: "mutate",
    confirm: false,
    guide: {
      useWhen: ["há uma dimensão nominal decidida no briefing, catálogo ou plano"],
      avoidWhen: ["a diferença observada é apenas entre volume fechado e envelope paramétrico", "a mesma medida já foi atingida"],
      prerequisites: ["definir a dimensão-alvo uma vez"],
      procedure: ["redimensionar", "reler dimensionsMm e renderedGeometry", "não repetir quando unchanged=true"],
      success: ["dimensionsMm atinge a intenção nominal e a instalação física passa separadamente"],
      recovery: ["se a fórmula limitar o resultado, aceite a medida atingida ou escolha outro produto; não envie a mesma medida novamente"],
    },
  }),
  defineCapability({
    name: "scene.element.paint",
    title: "Pintar elemento ou peça",
    group: "cena",
    plane: "client",
    summary: "Aplica um material do catálogo a módulos, paredes, pisos ou vãos. path nulo pinta o alvo inteiro; um caminho retornado pela leitura do módulo pinta somente aquela peça interna. Localize o material com catalog.item.search antes de usar.",
    progressLabel: "Aplicando o material…",
    input: z.object({
      elementIds: z.array(elementId).min(1).max(100),
      path: z.string().trim().min(1).max(1_000).nullable().default(null)
        .describe("Caminho da peça interna; null pinta cada alvo inteiro."),
      materialCatalogItemId: z.string().trim().min(1).max(160)
        .describe("ID de um item do catálogo cujo tipo é material."),
    }),
    output: z.object({ targets: z.array(z.record(z.string(), z.unknown())), materialName: z.string() }),
    effect: "mutate",
    confirm: false,
  }),
  defineCapability({
    name: "scene.element.appearance.match",
    title: "Igualar acabamento de referência",
    group: "cena",
    plane: "client",
    core: true,
    summary: "Copia material base e atribuições de acabamento de um elemento existente para outro. Use quando o pedido disser mesma cor, mesmo material ou mesmo acabamento de uma referência real; evita pesquisar novamente e impede IDs de material inventados.",
    progressLabel: "Igualando o acabamento…",
    input: z.object({ elementId, referenceElementId: elementId }),
    output: z.object({ element: elementSnapshot, referenceElementId: z.string(), unchanged: z.boolean() }),
    effect: "mutate",
    confirm: false,
    guide: {
      useWhen: ["a aparência deve ser igual à de um elemento existente"],
      avoidWhen: ["o usuário pediu um material diferente e nominal do catálogo"],
      prerequisites: ["alvo e referência reais e distintos"],
      procedure: ["copiar uma vez", "reler o alvo", "verificar visualmente"],
      success: ["materialId e materialAssignments equivalentes aos da referência"],
      recovery: ["não faça nova busca de material se a referência já é a autoridade pedida"],
    },
  }),
  defineCapability({
    name: "scene.element.delete",
    title: "Apagar elemento",
    group: "cena",
    plane: "client",
    summary: "Apaga os elementos indicados da cena. É destrutivo e só executa depois de confirmação explícita mostrando os nomes afetados. Para apenas esconder ou trocar a seleção, use outra capacidade.",
    progressLabel: "Apagando os itens confirmados…",
    input: z.object({ elementIds: z.array(elementId).min(1).max(100) }),
    output: z.object({
      deletedIds: z.array(z.string()),
      deletedNames: z.array(z.string()),
      deletedCreationIntents: z.array(z.object({
        stepId: z.string(),
        instanceKey: z.string(),
        catalogItemId: z.string(),
      })).optional(),
    }),
    effect: "destructive",
    confirm: true,
  }),
  defineCapability({
    name: "scene.element.read",
    title: "Conferir elemento",
    group: "conferência",
    plane: "client",
    summary: "Relê do store a origem paramétrica, o centro físico mundial, rotação, dimensões nominais/paramétricas, volume físico fechado, material e estado de um elemento depois de uma alteração. renderedGeometry.status=ready confirma geometria medida na revisão atual ou geometria paramétrica exata; pending identifica fallback de componente dinâmico e não deve fundamentar correções espaciais. renderedLocalBoundsMm permanece no eixo local; use physicalCenterMm para mover no mundo. Em componentes dinâmicos também devolve dynamic.interactions com os caminhos exatos de portas, gavetas e outras partes móveis.",
    progressLabel: "Conferindo o que ficou gravado…",
    input: z.object({ elementId }),
    output: z.object({ element: elementSnapshot, dynamic: dynamicElementInfo.optional() }),
    effect: "read",
    confirm: false,
    guide: {
      useWhen: ["é necessário confirmar estado gravado ou obter paths dinâmicos exatos"],
      avoidWhen: ["a mesma revisão já foi lida e nenhuma mutação ocorreu"],
      prerequisites: ["elementId real retornado pela cena ou por uma mutação"],
      procedure: ["ler uma vez por revisão", "usar dimensionsMm como nominal e renderedDimensionsMm somente quando status=ready"],
      success: ["revisão atual e autoridade geométrica explícita"],
      recovery: ["se pending, aguarde a sincronização; não redimensione para forçar a malha"],
    },
  }),
  defineCapability({
    name: "scene.check.clearance",
    title: "Conferir folgas e colisões",
    group: "conferência",
    plane: "client",
    summary: "Confere colisões e as folgas ortogonais em milímetros entre um módulo e paredes ou outros módulos visíveis. Use depois de inserir, mover, girar ou redimensionar.",
    progressLabel: "Conferindo folgas e encontros…",
    input: z.object({ elementId }),
    output: z.object({
      collisions: z.array(z.object({ kind: z.enum(["element", "wall"]), id: z.string(), name: z.string() })),
      clearancesMm: z.record(z.string(), z.number()),
      geometry: renderedGeometryStatus,
    }),
    effect: "read",
    confirm: false,
  }),
  defineCapability({
    name: "scene.part.setState",
    title: "Definir estado de partes móveis",
    group: "cena",
    plane: "client",
    core: true,
    summary: "Define de forma determinística o estado de todas as partes móveis correspondentes de um componente dinâmico. Use para fechar ou abrir portas, gavetas ou todas as interações sem adivinhar paths nem repetir toggles individuais.",
    progressLabel: "Ajustando as partes móveis…",
    input: z.object({
      elementId,
      selector: z.enum(["all", "doors", "drawers"]).default("all"),
      nameContains: z.string().trim().min(1).max(120).optional(),
      desiredState: z.enum(["open", "closed"]),
    }),
    output: z.object({
      element: elementSnapshot,
      matchedPaths: z.array(z.string()),
      changedPaths: z.array(z.string()),
      alreadySatisfiedPaths: z.array(z.string()),
      allMatchedSatisfied: z.boolean(),
      interactions: dynamicElementInfo.shape.interactions,
      changed: z.boolean(),
      unchanged: z.boolean(),
    }),
    effect: "mutate",
    confirm: false,
    guide: {
      useWhen: ["o pedido se aplica a um grupo de portas, gavetas ou todas as partes móveis"],
      avoidWhen: ["uma interação específica já foi identificada pelo path e somente ela deve mudar"],
      prerequisites: ["elemento dinâmico carregado"],
      procedure: ["selecionar o grupo", "definir estado absoluto", "conferir allMatchedSatisfied"],
      success: ["todas as partes correspondentes no estado pedido"],
      recovery: ["se nenhum path corresponder, releia nomes e use nameContains; não envie path vazio nem toggle"],
    },
  }),
  defineCapability({
    name: "scene.check.grounded",
    title: "Conferir apoio no piso",
    group: "conferência",
    plane: "client",
    summary: "Compara a base do elemento com a elevação do piso e informa se está apoiado, flutuando ou enterrado, com a diferença em milímetros. Módulos aéreos podem estar flutuando por intenção; relate o resultado em vez de corrigir sem contexto.",
    progressLabel: "Conferindo o apoio…",
    input: z.object({ elementId }),
    output: z.object({
      status: z.enum(["grounded", "floating", "buried"]),
      floorElevationMm: z.number(),
      gapMm: z.number(),
      geometry: renderedGeometryStatus,
    }),
    effect: "read",
    confirm: false,
  }),
  defineCapability({
    name: "scene.check.installation",
    title: "Conferir instalação e encontros",
    group: "conferência",
    plane: "client",
    summary: "Mede programaticamente apoio, fixação, teto e encontros com paredes e módulos próximos. support informa se o elemento está apoiado no piso, parede, teto ou outro módulo. nearestElements distingue separação, contato e sobreposição. Use junto da inspeção adaptativa, que confere as malhas realmente renderizadas.",
    progressLabel: "Conferindo apoio, fixação e encontros…",
    input: z.object({ elementId }),
    output: z.object({
      support: z.object({
        expected: z.enum(["wall", "floor", "ceiling", "element", "unconstrained"]),
        status: z.enum(["supported", "gap", "penetrating", "unsupported"]),
        surfaceId: z.string().nullable(),
        gapMm: z.number().nullable(),
      }),
      floor: z.object({
        status: z.enum(["grounded", "floating", "buried"]),
        floorElevationMm: z.number(),
        gapMm: z.number(),
      }),
      ceiling: z.object({
        status: z.enum(["touching", "clear", "penetrating"]),
        ceilingElevationMm: z.number(),
        gapMm: z.number(),
      }),
      nearestWalls: z.array(z.object({ wallId: z.string(), gapMm: z.number() })),
      nearestElements: z.array(z.object({
        elementId: z.string(),
        name: z.string(),
        planGapMm: z.number(),
        verticalGapMm: z.number(),
        planOverlapMm: z.number(),
        verticalOverlapMm: z.number(),
        distanceMm: z.number(),
        position: z.enum(["above", "below", "beside"]),
        relation: z.enum(["separate", "touching", "overlapping"]),
      })),
      geometry: renderedGeometryStatus,
    }),
    effect: "read",
    confirm: false,
  }),
  defineCapability({
    name: "catalog.item.search",
    title: "Buscar no catálogo",
    group: "catálogo",
    plane: "server",
    summary: "Busca módulos ou materiais publicados no catálogo do próprio cliente por nome, categoria ou apelido. Medidas de módulo e identidade de material vêm deste resultado, nunca da memória do modelo.",
    progressLabel: "Procurando no catálogo…",
    input: z.object({
      query: z.string().trim().min(1).max(160).describe("Nome, categoria, descrição ou apelido procurado."),
      type: z.enum(["architecture", "module", "material"]).optional(),
      limit: z.number().int().min(1).max(30).default(12),
    }),
    output: z.object({ items: z.array(z.record(z.string(), z.unknown())) }),
    effect: "read",
    confirm: false,
    handler: { kind: "rest", method: "GET", path: "/catalog-items", query: ["query", "type", "limit"] },
    guide: {
      useWhen: ["é necessário um produto ou material que ainda não possui ID confirmado"],
      avoidWhen: ["o ID já foi devolvido nesta tarefa", "a cor deve apenas copiar um elemento existente"],
      prerequisites: ["consultar com o tipo correto e termos do pedido, sem reinterpretar a categoria"],
      procedure: ["buscar uma vez", "comparar nome, tipo, medidas e confiança", "preservar o ID escolhido"],
      success: ["item escolhido pertence ao tipo solicitado e seu ID é reutilizado literalmente"],
      recovery: ["se vazio, simplifique termos mantendo a categoria; não fabrique IDs"],
    },
  }),
  defineCapability({
    name: "catalog.folder.list",
    title: "Listar pastas do catálogo",
    group: "catálogo",
    plane: "server",
    summary: "Lista a árvore de pastas ativas do catálogo do cliente, com caminho, tipo e quantidade de itens. Use quando a busca precisa ser orientada pela organização do catálogo.",
    progressLabel: "Consultando as pastas do catálogo…",
    input: z.object({ kind: z.enum(["module", "material"]).optional() }),
    output: z.object({ folders: z.array(z.record(z.string(), z.unknown())) }),
    effect: "read",
    confirm: false,
    handler: { kind: "rest", method: "GET", path: "/catalog-folders", query: ["kind"] },
  }),
  defineCapability({
    name: "ui.ask",
    title: "Perguntar ao projetista",
    group: "interface",
    plane: "client",
    summary: "Suspende o trabalho para esclarecer uma decisão que mudaria materialmente o projeto e retoma o mesmo turno com a resposta. Use no começo da dúvida, com 2 a 4 opções quando elas forem claras; não use para escolhas pequenas.",
    progressLabel: "Precisando alinhar uma decisão…",
    input: z.object({
      subject: z.string().trim().min(3).max(120)
        .describe("Nome curto e específico do que está sendo decidido, por exemplo 'Material do aéreo basculante'. Não repita o pedido inteiro."),
      reason: z.string().trim().min(10).max(240)
        .describe("Explique o fato que bloqueou a etapa: o que não foi encontrado ou comprovado, em qual alvo e por que a decisão é necessária."),
      question: z.string().trim().min(1).max(500)
        .describe("Uma única pergunta objetiva sobre a decisão nomeada em subject. Prefira até 200 caracteres; 500 é o teto de segurança. Nunca escreva apenas 'qual item devo usar?'."),
      options: z.array(z.string().trim().min(1).max(120)).min(2).max(4).optional(),
      allowFreeText: z.boolean().default(true),
    }),
    output: z.object({ answer: z.string() }),
    effect: "read",
    confirm: false,
  }),
  defineCapability({
    name: "task.plan.propose",
    title: "Preparar o plano",
    group: "orquestração",
    plane: "client",
    summary: "Registra uma única proposta compacta depois da descoberta: intenção, restrições, decisões livres já resolvidas, entregas e critérios verificáveis. designResolutions é sempre obrigatório: use [] somente sem variáveis livres; caso contrário cubra cada freeVariable com a escolha concreta anterior à mutação. Produto livre informa targetCatalogItemId; dimensão livre, targetDimensionsMm nominal/paramétrico; acabamento livre, targetMaterialCatalogItemId ou targetReferenceElementId. O volume físico é validado por instalação e folgas. Não use 'a definir na execução'. Em tarefas standard, agrupe o trabalho em 2 a 5 etapas. Depois de aceito, o plano fica bloqueado.",
    progressLabel: "Organizando o plano…",
    input: taskPlanProposalSchema,
    output: z.object({ accepted: z.boolean(), revision: z.number().int().positive(), approvedByUser: z.boolean() }),
    effect: "read",
    confirm: false,
  }),
  defineCapability({
    name: "task.plan.progress",
    title: "Atualizar o plano",
    group: "orquestração",
    plane: "client",
    summary: "Atualiza uma entrega do plano com estado e evidências observadas. O CalcPlan marca automaticamente a etapa em andamento na primeira ação; conclua-a após readback e verificações estruturadas. A conferência de conclusão aponta divergências uma única vez, com o caminho de correção; se você concluir novamente sem resolver, a entrega fecha com a pendência registrada para a revisão final — prefira resolver, mas o fluxo nunca trava. A auditoria visual global ocorre automaticamente depois de todas as entregas e não deve impedir esta atualização. Se houver um impedimento material, faça uma pergunta específica para essa entrega.",
    progressLabel: "Atualizando o andamento…",
    input: taskPlanProgressInputSchema,
    output: z.object({ updated: z.boolean(), completed: z.number().int().nonnegative(), total: z.number().int().positive() }),
    effect: "read",
    confirm: false,
  }),
  defineCapability({
    name: "task.plan.amend",
    title: "Emendar uma decisão espacial do plano",
    group: "orquestração",
    plane: "client",
    core: true,
    summary: "Emenda uma única spatialDecision do plano vigente durante a execução, com justificativa e evidências, quando a leitura, a conferência de instalação, o laudo visual ou a regra de tipologia mostram que a folga, o suporte ou o contato registrados não podem ser executados ou não são profissionais (por exemplo, um elemento suspenso com folga zero sobre o móvel de baixo, ou um vão abaixo do mínimo da tipologia). A emenda é validada pelas mesmas regras de coerência do plano, não pode contrariar uma relação explicitamente pedida pelo projetista e fica registrada na auditoria. Quando o mesmo subject tem mais de uma decisão, a emenda usa relation e reference para desambiguar; se ainda assim for ambígua, a mensagem lista as candidatas com decisionIndex — reenvie escolhendo por decisionIndex, nunca repita a emenda idêntica. Use antes de corrigir o elemento; nunca reinsira o módulo nem recrie o plano.",
    progressLabel: "Emendando a decisão de projeto…",
    input: taskPlanAmendmentInputSchema,
    output: z.object({
      amended: z.boolean(),
      revision: z.number().int().positive(),
      changedFields: z.array(z.string()),
      decision: z.record(z.string(), z.unknown()),
    }),
    effect: "read",
    confirm: false,
    guide: {
      useWhen: ["o laudo ou a conferência devolveram [emenda-de-plano-necessária]", "a folga ou o suporte planejados violam a tipologia do elemento", "a implantação planejada é geometricamente inviável"],
      avoidWhen: ["a decisão foi definida explicitamente pelo projetista", "o problema é de execução, não do contrato do plano"],
      prerequisites: ["plano aceito e etapa em execução", "evidência concreta da inviabilidade ou do defeito profissional"],
      procedure: ["emendar uma vez com a nova folga/suporte", "reaplicar a implantação com scene.element.place", "reler e revisar"],
      success: ["amended=true e revisão do plano incrementada"],
      recovery: ["se a emenda for recusada, corrija os campos indicados; não tente contornar o plano com nudges sucessivos"],
    },
  }),
  defineCapability({
    name: "task.plan.review",
    title: "Revisão de prancheta",
    group: "orquestração",
    plane: "client",
    summary: "Registra o parecer do revisor independente de prancheta sobre o plano proposto, antes da execução. Uso exclusivo da chamada de revisão: o revisor recebe o brief em orchestration.planReview e responde com verdict sign ou object. A objeção é única e precisa estar ancorada nas leituras (cota em mm, linha da parede, elemento ou premissa); objeção sem âncora é registrada como observação e não bloqueia.",
    progressLabel: "Revisando a prancheta…",
    input: planDesignReviewInputSchema,
    output: z.object({ recorded: z.boolean() }),
    effect: "read",
    confirm: false,
  }),
] as const;

export const ALL_CAPABILITIES: readonly CapabilityDefinition[] = [
  ...CORE_CAPABILITIES,
  ...EXTENDED_CAPABILITIES,
];

export const ALWAYS_AVAILABLE_CAPABILITIES: readonly CapabilityDefinition[] = [
  ...CORE_CAPABILITIES,
  ...EXTENDED_CAPABILITIES.filter((capability) => capability.core),
];

const capabilityByName = new Map(ALL_CAPABILITIES.map((capability) => [capability.name, capability]));

if (capabilityByName.size !== ALL_CAPABILITIES.length) {
  throw new Error("O Registro de Capacidades contém nomes duplicados.");
}

export function getCapability(name: string): CapabilityDefinition | undefined {
  return capabilityByName.get(name);
}

/**
 * Resolve nomes emitidos pelo provedor sem transformar uma aproximação em uma
 * mutação. O formato com `__` é apenas a codificação de transporte. Já aliases
 * semânticos só são aceitos quando apontam, de forma inequívoca, para uma
 * capacidade de leitura.
 */
export function resolveCapabilityName(
  name: string,
  options: { allowSemanticReadAlias?: boolean } = {},
): CapabilityNameResolution | undefined {
  const trimmed = name.trim();
  if (capabilityByName.has(trimmed)) return { name: trimmed, strategy: "exact" };

  const providerDecoded = trimmed.replaceAll("__", ".");
  if (capabilityByName.has(providerDecoded)) {
    return { name: providerDecoded, strategy: "provider-encoding" };
  }
  if (!options.allowSemanticReadAlias) return undefined;

  const queryTokens = capabilityNameTokens(providerDecoded);
  if (queryTokens.length < 2) return undefined;
  const querySet = new Set(queryTokens);
  const ranked = ALL_CAPABILITIES
    .filter((capability) => capability.effect === "read")
    .map((capability) => {
      const candidateTokens = capabilityNameTokens(capability.name);
      const candidateSet = new Set(candidateTokens);
      const shared = queryTokens.filter((token) => candidateSet.has(token)).length;
      const sameOperation = queryTokens.at(-1) === candidateTokens.at(-1);
      const score = shared / querySet.size
        + shared / candidateSet.size
        + (sameOperation ? 0.4 : 0)
        + (queryTokens[0] === candidateTokens[0] ? 0.2 : 0);
      return { capability, shared, sameOperation, score };
    })
    .filter((entry) => entry.shared >= 2 && entry.sameOperation)
    .sort((left, right) => right.score - left.score || left.capability.name.localeCompare(right.capability.name));
  const best = ranked[0];
  if (!best || (ranked[1] && best.score - ranked[1].score < 0.2)) return undefined;
  return { name: best.capability.name, strategy: "semantic-read-alias" };
}

export function parseCapabilityInput(name: string, input: unknown): unknown {
  const capability = getCapability(name);
  if (!capability) throw new Error(`Capacidade desconhecida: ${name}.`);
  return capability.input.parse(input);
}

/**
 * Valida argumentos produzidos pelo modelo sem deixar o erro bruto do Zod
 * escapar para a interface. A falha estruturada pode voltar ao modelo para que
 * ele corrija a mesma chamada na etapa seguinte.
 */
export function validateCapabilityInput(name: string, input: unknown): CapabilityInputValidation {
  const capability = getCapability(name);
  if (!capability) {
    return {
      valid: false,
      error: {
        code: "UNKNOWN_CAPABILITY",
        capability: name,
        message: `A capacidade ${name} não existe no registro atual. Escolha uma capacidade disponível.`,
        issues: [],
        retryable: true,
      },
    };
  }
  const parsed = capability.input.safeParse(input);
  if (parsed.success) return { valid: true, data: parsed.data };
  return {
    valid: false,
    error: {
      code: "INVALID_CAPABILITY_INPUT",
      capability: name,
      message: `Os argumentos de ${name} não respeitam o contrato. Reenvie a chamada completa, preservando os campos válidos e corrigindo os campos indicados; não envie somente um patch parcial.`,
      issues: parsed.error.issues.slice(0, 12).map((issue) => ({
        path: issue.path.map(String).join(".") || "input",
        code: issue.code,
        message: issue.message,
      })),
      retryable: true,
    },
  };
}

export function parseCapabilityOutput(name: string, output: unknown): unknown {
  const capability = getCapability(name);
  if (!capability) throw new Error(`Capacidade desconhecida: ${name}.`);
  return capability.output.parse(output);
}

export function coreToolDefinitions(): AgentToolDefinition[] {
  return toolDefinitions(ALWAYS_AVAILABLE_CAPABILITIES);
}

export function allToolDefinitions(): AgentToolDefinition[] {
  return toolDefinitions(ALL_CAPABILITIES);
}

export function capabilityToolDefinition(capability: CapabilityDefinition): AgentToolDefinition {
  return {
    name: capability.name,
    description: capabilityDescription(capability),
    inputSchema: z.toJSONSchema(capability.input, { target: "draft-7" }) as Record<string, unknown>,
  };
}

export function capabilityDescription(capability: CapabilityDefinition): string {
  const sections = [capability.summary];
  const guide = operationalGuideForCapability(capability);
  if (guide?.useWhen.length) sections.push(`Use quando: ${guide.useWhen.join("; ")}.`);
  if (guide?.avoidWhen?.length) sections.push(`Não use quando: ${guide.avoidWhen.join("; ")}.`);
  if (guide?.prerequisites?.length) sections.push(`Antes: ${guide.prerequisites.join("; ")}.`);
  if (guide?.procedure?.length) sections.push(`Procedimento: ${guide.procedure.join(" → ")}.`);
  if (guide?.success?.length) sections.push(`Sucesso: ${guide.success.join("; ")}.`);
  if (guide?.recovery?.length) sections.push(`Recuperação: ${guide.recovery.join("; ")}.`);
  if (capability.examples?.length) {
    sections.push(`Exemplo: ${capability.examples.slice(0, 2).map((example) => (
      `${example.request} => ${JSON.stringify(example.input)}`
    )).join(" | ")}.`);
  }
  return sections.join(" ").slice(0, 4_000);
}

/**
 * Every discovered action carries a minimum execution protocol. Domain-specific
 * capabilities can override it with a richer guide, while less common actions
 * still teach the model how to avoid blind retries and how to prove success.
 */
export function operationalGuideForCapability(capability: CapabilityDefinition): CapabilityOperationalGuide {
  if (capability.guide) return capability.guide;
  if (capability.effect === "read") {
    return {
      useWhen: [`os dados de ${capability.title.toLocaleLowerCase("pt-BR")} forem necessários para a próxima decisão`],
      procedure: ["executar uma vez com o escopo mínimo", "reutilizar o resultado enquanto a revisão da cena não mudar"],
      success: ["o retorno fornece evidência suficiente para a próxima ação"],
      recovery: ["se faltar um identificador, obtê-lo por leitura ou busca; nunca inventá-lo"],
    };
  }
  return {
    useWhen: [`o plano vigente exigir ${capability.title.toLocaleLowerCase("pt-BR")}`],
    avoidWhen: ["a mesma intenção já foi aplicada e o estado retornado a confirma"],
    prerequisites: ["usar somente identificadores e valores confirmados por leitura, catálogo ou plano"],
    procedure: ["executar uma vez", "conferir o estado devolvido", "só corrigir com uma estratégia diferente e evidência nova"],
    success: ["o retorno confirma o resultado pretendido sem ampliar o escopo"],
    recovery: ["em erro, preservar campos válidos e corrigir exatamente o campo indicado; não repetir às cegas"],
  };
}

export function toolDefinitions(capabilities: readonly CapabilityDefinition[]): AgentToolDefinition[] {
  return capabilities.map(capabilityToolDefinition);
}

export function searchCapabilities(query: string, limit = 12): CapabilityDefinition[] {
  const terms = normalizeSearch(query).split(/\s+/).filter(Boolean);
  return ALL_CAPABILITIES
    .filter((capability) => !["capability.search", "capability.invoke"].includes(capability.name))
    .map((capability) => {
      const searchable = normalizeSearch([
        capability.name,
        capability.title,
        capability.group,
        capability.summary,
      ].join(" "));
      const score = terms.reduce((total, term) => total + (searchable.includes(term) ? 1 : 0), 0);
      return { capability, score };
    })
    .filter((entry) => !terms.length || entry.score > 0)
    .sort((left, right) => right.score - left.score || left.capability.name.localeCompare(right.capability.name))
    .slice(0, Math.max(1, Math.min(30, limit)))
    .map((entry) => entry.capability);
}

/** Provider tool identifiers do not accept dots; the product-facing registry does. */
export function encodeProviderToolName(name: string): string {
  return name.replaceAll(".", "__");
}

export function decodeProviderToolName(name: string): string {
  const decoded = name.replaceAll("__", ".");
  return capabilityByName.has(decoded) ? decoded : name;
}

function capabilityNameTokens(value: string): string[] {
  return [...new Set(normalizeSearch(value).split(/[^a-z0-9]+/u).filter(Boolean))];
}

function normalizeSearch(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("pt-BR");
}
